/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: admin
 *  创建时间: 2026-08-28 09:06
 *  文件名: src/components/ppt-learning-plan-viewer/ppt-viewer/ppt-images-viewer.tsx
 *  功能描述: PPT 幻灯片预览组件，纵向轮播展示幻灯片，支持缩略图联动与键盘 ↑↓ 翻页
 */
import React, { useEffect, useRef, useState } from 'react';
import useEmblaCarousel, { EmblaOptionsType } from 'embla-carousel-react';
import styles from './ppt-images-viewer.module.less';
import { useStableCallbackV2 } from 'eastedu-kit';

/** 轮播配置：纵向滚动、一次翻一页、所有页（含首尾页）居中对齐 */
const CAROUSEL_OPTIONS: EmblaOptionsType = {
  axis: 'y',
  slidesToScroll: 1,
  align: 'center',
};

/** 滚轮翻页触发阈值（累积滚动像素量），超过后触发一次翻页 */
const WHEEL_TRIGGER_THRESHOLD = 30;

/** 滚轮翻页冷却时长（毫秒），防止触摸板惯性滚动连续触发多页翻页 */
const WHEEL_COOLDOWN_MS = 250;

interface PPTImagesViewerProps {
  /** 幻灯片图片地址列表 */
  slides: string[];
  /** 当前页变化时通知零基页索引（点击、键盘、滚轮等任意切换方式） */
  onSlideChange?: (index: number) => void;
}

/**
 * 生成主预览区幻灯片按钮的无障碍描述
 */
function getSlideAriaLabel(index: number, selectedIndex: number) {
  const pageNo = index + 1;
  if (index === selectedIndex) {
    return `当前第 ${pageNo} 页`;
  }
  return index < selectedIndex ? `上一页，第 ${pageNo} 页` : `下一页，第 ${pageNo} 页`;
}

/**
 * PPT预览器(通过图片进行预览)
 */
function PPTImagesViewer(props: PPTImagesViewerProps) {
  const { slides, onSlideChange } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(CAROUSEL_OPTIONS);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const thumbListRef = useRef<HTMLOListElement>(null);

  const onSlideChangeStable = useStableCallbackV2((idx) => {
    onSlideChange?.(idx);
  });

  /**
   * 当前页变化时，通知父组件当前页索引，并让左侧缩略图列表滚动到对应缩略图尽量居中展示
   */
  useEffect(() => {
    const listEl = thumbListRef.current;
    if (!listEl) {
      return;
    }
    const itemEl = listEl.children[selectedIndex] as HTMLElement | undefined;
    if (!itemEl) {
      return;
    }

    onSlideChangeStable?.(selectedIndex);

    listEl.scrollTop = itemEl.offsetTop - (listEl.clientHeight - itemEl.offsetHeight) / 2;
  }, [selectedIndex]);

  /**
   * 订阅轮播选中及重初始化事件，同步当前页码状态
   */
  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  /**
   * 监听键盘 ↑↓ 键快捷翻页，输入类控件聚焦时不拦截
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }
      const targetEl = event.target as HTMLElement | null;
      if (
        targetEl &&
        (targetEl.tagName === 'INPUT' ||
          targetEl.tagName === 'TEXTAREA' ||
          targetEl.isContentEditable)
      ) {
        return;
      }
      if (!emblaApi) {
        return;
      }
      event.preventDefault();
      if (event.key === 'ArrowUp') {
        emblaApi.scrollPrev();
      } else {
        emblaApi.scrollNext();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [emblaApi]);

  /**
   * 监听鼠标滚轮切换幻灯片：累积滚动量达到阈值后翻页一次，冷却期内忽略，
   * 避免触摸板惯性滚动一次触发多页
   */
  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    const viewportEl = emblaApi.rootNode();
    let wheelLock = false;
    let wheelAccumulator = 0;
    let unlockTimer = 0;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (wheelLock) {
        return;
      }
      wheelAccumulator += event.deltaY;
      if (Math.abs(wheelAccumulator) < WHEEL_TRIGGER_THRESHOLD) {
        return;
      }
      if (wheelAccumulator > 0) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollPrev();
      }
      wheelAccumulator = 0;
      wheelLock = true;
      unlockTimer = window.setTimeout(() => {
        wheelLock = false;
      }, WHEEL_COOLDOWN_MS);
    };
    viewportEl.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.clearTimeout(unlockTimer);
      viewportEl.removeEventListener('wheel', onWheel);
    };
  }, [emblaApi]);

  /**
   * 跳转到指定页
   */
  const scrollToSlide = (index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  };

  return (
    <div className={styles.ppt_images_viewer}>
      <ol className={styles.thumbs} ref={thumbListRef} aria-label="幻灯片缩略图">
        {slides.map((slide, index) => {
          const isActive = index === selectedIndex;
          const itemClass = isActive ? `${styles.thumb_item} ${styles.active}` : styles.thumb_item;
          return (
            <li
              key={`${slide}-${index}`}
              className={itemClass}
              onClick={() => scrollToSlide(index)}
            >
              <img
                className={styles.thumb_img}
                src={slide}
                alt={`第 ${index + 1} 页`}
                loading="lazy"
              />
              <span className={styles.thumb_page}>{index + 1}</span>
            </li>
          );
        })}
      </ol>
      <div className={styles.main}>
        <div className={styles.viewport} ref={emblaRef}>
          <div className={styles.container}>
            {slides.map((slide, index) => {
              const isActive = index === selectedIndex;
              const btnClass = isActive ? `${styles.slide_btn} ${styles.active}` : styles.slide_btn;
              const slideClass = isActive ? `${styles.slide} ${styles.active}` : styles.slide;
              return (
                <div className={slideClass} key={`${slide}-${index}`}>
                  <button
                    type="button"
                    className={btnClass}
                    aria-label={getSlideAriaLabel(index, selectedIndex)}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => scrollToSlide(index)}
                  >
                    <img
                      className={styles.slide_img}
                      src={slide}
                      alt={`PPT 第 ${index + 1} 页`}
                      loading="lazy"
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.tip} role="status">
          键盘 ↑↓ 键快捷翻 PPT
        </div>
      </div>
    </div>
  );
}

export { PPTImagesViewer };
export type { PPTImagesViewerProps };
